import type { MaterialityFilter } from '../../hooks/useMaterialityFilter';

/**
 * Segmented control to filter the field grid by materiality.
 *
 * Spec: docs/archive/pre-cut-2026-08/phase-b-deals-card-fields-spec.md §6.3.
 *
 * Renders three buttons (all / changes / material). The "Show material"
 * option only appears when at least one field on the deal carries
 * `materiality='not_material'` — otherwise it's a no-op control and
 * we hide it to keep the bar uncluttered.
 */

interface MaterialityControlProps {
  value: MaterialityFilter;
  onChange: (next: MaterialityFilter) => void;
  /** When false, hides the "Material only" option entirely. */
  hasNonMaterialProposals: boolean;
  /** Pass-through styling. */
  className?: string;
  buttonClassName?: string;
  buttonActiveClassName?: string;
}

const OPTIONS: { value: MaterialityFilter; label: string; title: string }[] = [
  { value: 'all',      label: 'All',      title: 'Show every proposed field' },
  { value: 'changes',  label: 'Changes',  title: 'Show fields where the value changed' },
  { value: 'material', label: 'Material', title: 'Show only material-marked changes' },
];

export function MaterialityControl({
  value,
  onChange,
  hasNonMaterialProposals,
  className,
  buttonClassName,
  buttonActiveClassName,
}: MaterialityControlProps) {
  const visible = OPTIONS.filter((opt) =>
    opt.value !== 'material' || hasNonMaterialProposals,
  );

  return (
    <div className={className} role="group" aria-label="Materiality filter">
      {visible.map((opt) => {
        const isActive = value === opt.value;
        const cls = isActive && buttonActiveClassName
          ? `${buttonClassName ?? ''} ${buttonActiveClassName}`
          : buttonClassName;
        return (
          <button
            key={opt.value}
            type="button"
            className={cls}
            title={opt.title}
            aria-pressed={isActive}
            onClick={() => onChange(opt.value)}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
